import React, { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useEstateContext } from "@/lib/EstateContext";
import { estatesService, usersService } from "@/services";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Building2, Loader2, AlertCircle, MapPin, CheckCircle2, Plus } from "lucide-react";
import { motion } from "framer-motion";

export default function CreateEstate() {
  const { user, isLoadingAuth } = useAuth();
  const { refreshEstates } = useEstateContext();
  const FORM_CACHE_KEY = 'create_estate_form_draft';

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [createdEstates, setCreatedEstates] = useState([]); // names created this session
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState(() => {
    // Restore any in-progress draft so the form survives background re-renders
    try {
      const saved = sessionStorage.getItem(FORM_CACHE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (_) {}
    return { estateName: "", location: "", unitCount: "" };
  });

  const handleChange = (e) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    setFormData(updated);
    // Persist draft so the form survives any background auth re-renders
    try { sessionStorage.setItem(FORM_CACHE_KEY, JSON.stringify(updated)); } catch (_) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Guard: user must be loaded before we can create an estate
    if (!user?.id) {
      setError("Still loading your account — please wait a moment and try again.");
      return;
    }

    setIsLoading(true);

    try {
      // Validation
      if (!formData.estateName.trim()) {
        setError("Estate name is required");
        setIsLoading(false);
        return;
      }

      if (formData.estateName.length < 3) {
        setError("Estate name must be at least 3 characters");
        setIsLoading(false);
        return;
      }

      if (!formData.unitCount || formData.unitCount < 1) {
        setError("Number of units is required and must be at least 1");
        setIsLoading(false);
        return;
      }

      if (formData.unitCount > 10000) {
        setError("Number of units cannot exceed 10,000. Please contact us for custom pricing.");
        setIsLoading(false);
        return;
      }

      // Create the estate
      const trialStartsAt = new Date().toISOString();
      const trialEndsAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 days from now
      
      // Automatically assign tier based on unit count
      const unitCount = parseInt(formData.unitCount, 10);
      let subscriptionTier;
      if (unitCount <= 50) {
        subscriptionTier = 'small';
      } else if (unitCount <= 150) {
        subscriptionTier = 'medium';
      } else if (unitCount <= 300) {
        subscriptionTier = 'large';
      } else {
        subscriptionTier = 'custom';
      }
      
      const newEstate = await estatesService.create(
        {
          name: formData.estateName.trim(),
          location: formData.location.trim() || null,
          unit_count: unitCount,
          subscription_status: 'trial',
          subscription_tier: subscriptionTier,
          trial_starts_at: trialStartsAt,
          trial_ends_at: trialEndsAt,
        },
        user.id
      );

      console.log("✅ Estate created with 30-day trial:", newEstate);

      // Non-critical post-creation steps — log failures but don't block redirect
      try { await estatesService.addMember(newEstate.id, user.id, 'admin'); } catch (e) { console.error('addMember failed (non-fatal):', e); }
      // Only set the legacy estate_id if the user doesn't have one yet.
      // For additional estates, the source of truth is estate_members — don't
      // overwrite estate_id or it will become stale/null when that estate is deleted.
      if (!user.estate_id) {
        try { await usersService.updateMe({ estate_id: newEstate.id }); } catch (e) { console.error('updateMe failed (non-fatal):', e); }
      }
      try { sessionStorage.removeItem(FORM_CACHE_KEY); } catch (_) {}

      // Kick off context refresh without awaiting
      refreshEstates().catch(console.error);

      const createdName = formData.estateName.trim();
      setCreatedEstates(prev => [...prev, createdName]);
      setFormData({ estateName: "", location: "", unitCount: "" });
      setShowSuccess(true);
      setIsLoading(false);
    } catch (err) {
      console.error("❌ Error creating estate:", err);
      setError(
        err.message ||
          "Failed to create estate. Please try again or contact support."
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 flex items-center justify-center p-4">
      <motion.div
        key="create-estate-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl"
      >
        <Card className="p-8 md:p-12 shadow-2xl bg-white/95 backdrop-blur-sm">
          {showSuccess ? (
            /* ── Success / "What's next?" screen ── */
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-center py-4"
            >
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {createdEstates.length === 1 ? "Estate Created!" : `${createdEstates.length} Estates Created!`}
              </h2>
              <p className="text-gray-500 mb-6">
                {createdEstates.length === 1
                  ? "Your first estate is ready. Add more or head to your dashboard."
                  : `You've set up ${createdEstates.length} estates. Add more or head to your dashboard.`}
              </p>

              {/* Created estates chips */}
              <div className="flex flex-wrap gap-2 justify-center mb-8">
                {createdEstates.map((name, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-sm font-medium text-green-800"
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    {name}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  onClick={() => setShowSuccess(false)}
                  variant="outline"
                  className="flex-1 h-12 text-base border-teal-300 text-teal-700 hover:bg-teal-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another Estate
                </Button>
                <Button
                  type="button"
                  onClick={() => { window.location.href = '/EstateDashboard'; }}
                  className="flex-1 h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 shadow-lg"
                >
                  Continue to Dashboard →
                </Button>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">
                  Need help?{" "}
                  <a href="mailto:support@fixmyhood.app" className="text-teal-600 hover:text-teal-700 font-medium">
                    Contact Support
                  </a>
                </p>
              </div>
            </motion.div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {createdEstates.length > 0 ? "Add Another Estate" : "Create Your Estate"}
                </h1>
                <p className="text-gray-600">
                  {createdEstates.length > 0
                    ? "Set up an additional estate to manage under your account"
                    : "Set up your estate to start managing service requests and your community"}
                </p>
              </div>

              {/* First-time info box — only shown before any estate is created */}
              {createdEstates.length === 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">
                        You don't have an estate yet
                      </h3>
                      <p className="text-sm text-blue-800">
                        Create your estate below to access your dashboard and start managing
                         internal issues and reports within your community.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Alert */}
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Estate Name - Required */}
                <div className="space-y-2">
                  <Label htmlFor="estateName" className="text-base font-semibold">
                    Estate Name <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="estateName"
                      name="estateName"
                      type="text"
                      placeholder="e.g., North Oakes Estate, Green Valley Complex"
                      value={formData.estateName}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="pl-11 h-12 text-base text-gray-900 bg-white placeholder:text-gray-400 dark:text-gray-900 dark:bg-white dark:placeholder:text-gray-400"
                      required
                      autoFocus
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    The name of your residential estate or complex
                  </p>
                </div>

                {/* Location - Optional */}
                <div className="space-y-2">
                  <Label htmlFor="location" className="text-base font-semibold">
                    City or Suburb <span className="text-gray-400">(Optional)</span>
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="location"
                      name="location"
                      type="text"
                      placeholder="e.g., Cape Town, Johannesburg, Durban"
                      value={formData.location}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="pl-11 h-12 text-base text-gray-900 bg-white placeholder:text-gray-400 dark:text-gray-900 dark:bg-white dark:placeholder:text-gray-400"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    Help residents identify your estate's location
                  </p>
                </div>

                {/* Unit Count - Required */}
                <div className="space-y-2">
                  <Label htmlFor="unitCount" className="text-base font-semibold">
                    Number of Units <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="unitCount"
                    name="unitCount"
                    type="number"
                    min="1"
                    max="10000"
                    placeholder="e.g., 50, 120, 250"
                    value={formData.unitCount}
                    onChange={handleChange}
                    disabled={isLoading}
                    className="h-12 text-base text-gray-900 bg-white placeholder:text-gray-400 dark:text-gray-900 dark:bg-white dark:placeholder:text-gray-400"
                    required
                  />
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-sm text-emerald-900 font-medium mb-1">
                      💡 Pricing based on estate size:
                    </p>
                    <ul className="text-xs text-emerald-800 space-y-0.5 ml-4">
                      <li>• Up to 50 units: <span className="font-semibold">R599/month</span></li>
                      <li>• 51-150 units: <span className="font-semibold">R1,149/month</span></li>
                      <li>• 151-300 units: <span className="font-semibold">R1,999/month</span></li>
                    </ul>
                    <p className="text-xs text-emerald-700 mt-2 font-medium">
                      ✨ Start your <span className="font-bold">30-day free trial</span> — no credit card required!
                    </p>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <div className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-teal-900">
                      <p className="font-medium mb-1">What happens next?</p>
                      <ul className="space-y-1 text-teal-800">
                        <li>• Your estate will be created instantly</li>
                        <li>• You'll be set as the owner and administrator</li>
                        <li>• You can add more estates or continue to your dashboard</li>
                        <li>• You can manage issues, reports and contractors within your community</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 shadow-lg"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Estate...
                    </>
                  ) : (
                    <>
                      <Building2 className="w-5 h-5 mr-2" />
                      Create Estate
                    </>
                  )}
                </Button>
              </form>

              {/* Footer Note */}
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">
                  Need help?{" "}
                  <a
                    href="mailto:support@fixmyhood.app"
                    className="text-teal-600 hover:text-teal-700 font-medium"
                  >
                    Contact Support
                  </a>
                </p>
              </div>
            </>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
